import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import ImageIcon from '@mui/icons-material/Image';
import { filesApi } from '../services/api';

const PAGE_SIZE = 24;
const fileFolderId = (file) => file.document_folder_id ?? file.photo_folder_id ?? null;
const isImage = (name = '') => /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(name);
const fileDisplayName = (file) => {
  if (file?.file_type !== 'plans') return file?.file_name || '';
  const extensionIndex = (file.file_name || '').lastIndexOf('.');
  return extensionIndex > 0 ? file.file_name.slice(0, extensionIndex) : file.file_name;
};

export function FileThumbnail({ file }) {
  const [url, setUrl] = useState(file.url || '');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isImage(file.file_name) || file.url) return undefined;
    let active = true;
    filesApi.getUrl(file.file_id).then((response) => {
      if (active) setUrl(response.data?.url || '');
    }).catch(() => {
      if (active) setFailed(true);
    });
    return () => { active = false; };
  }, [file.file_id, file.file_name, file.url]);

  if (!isImage(file.file_name)) return <DescriptionIcon sx={{ fontSize: 48 }} color="action" />;
  if (failed) return <ImageIcon sx={{ fontSize: 48 }} color="action" />;
  if (!url) return <CircularProgress size={24} />;
  return <Box component="img" src={url} alt={file.file_name} onError={() => setFailed(true)}
    sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
}

export default function ActivityFilePicker({ open, onClose, onAttach, files = [], folders = [], loading = false }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [section, setSection] = useState(null);
  const [folderId, setFolderId] = useState(undefined);
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!open) {
      setSection(null);
      setFolderId(undefined);
      setSelectedIds([]);
      setPage(1);
    }
  }, [open]);

  const sectionFiles = useMemo(() => files.filter((file) => file.file_type === section), [files, section]);
  const folderFiles = useMemo(() => sectionFiles
    .filter((file) => fileFolderId(file) === folderId)
    .sort((a, b) => new Date(b.uploaded_date || 0) - new Date(a.uploaded_date || 0)),
  [sectionFiles, folderId]);
  const displayedFiles = folderFiles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selectedFiles = files.filter((file) => selectedIds.includes(file.file_id));
  const selectedFolder = folders.find((folder) => folder.document_folder_id === folderId);

  const back = () => {
    if (folderId !== undefined) setFolderId(undefined);
    else setSection(null);
    setPage(1);
  };
  const enterFolder = (id) => {
    setFolderId(id);
    setPage(1);
  };
  const toggleFile = (fileId) => setSelectedIds((current) => current.includes(fileId)
    ? current.filter((id) => id !== fileId)
    : [...current, fileId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen} aria-labelledby="activity-file-picker-title">
      <DialogTitle id="activity-file-picker-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {section && <IconButton onClick={back} aria-label="Back"><ArrowBackIcon /></IconButton>}
        <Typography component="span" variant="h6" sx={{ flex: 1 }}>
          {folderId !== undefined ? `${section === 'plans' ? 'Plans' : 'Photos'} / ${selectedFolder?.name || 'Unfiled'}`
            : section ? `Choose a ${section === 'plans' ? 'plan' : 'photo'} folder` : 'Choose plans or photos'}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close file picker"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 360 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>
        ) : !section ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2 }}>
            {[
              { type: 'plans', label: 'Plans', icon: <DescriptionIcon sx={{ fontSize: 58 }} /> },
              { type: 'photos', label: 'Photos', icon: <ImageIcon sx={{ fontSize: 58 }} /> },
            ].map(({ type, label, icon }) => (
              <Button key={type} variant="outlined" onClick={() => setSection(type)}
                sx={{ minHeight: 150, display: 'flex', flexDirection: 'column', gap: 1, textTransform: 'none' }}>
                {icon}
                <Typography variant="subtitle1">{label}</Typography>
                <Typography variant="caption">{files.filter((file) => file.file_type === type).length} available</Typography>
              </Button>
            ))}
          </Box>
        ) : folderId === undefined ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2 }}>
            {[
              ...folders.filter((folder) => folder.file_type === section).map((folder) => ({
                id: folder.document_folder_id,
                name: folder.name,
              })),
              { id: null, name: 'Unfiled' },
            ].map((folder) => {
              const count = sectionFiles.filter((file) => fileFolderId(file) === folder.id).length;
              if (count === 0) return null;
              return (
                <Button key={folder.id ?? 'unfiled'} variant="outlined" onClick={() => enterFolder(folder.id)}
                  sx={{ minHeight: 140, display: 'flex', flexDirection: 'column', gap: 1, textTransform: 'none' }}>
                  <FolderIcon sx={{ fontSize: 56 }} />
                  <Typography variant="subtitle1" align="center">{folder.name}</Typography>
                  <Typography variant="caption">{count} {count === 1 ? 'file' : 'files'}</Typography>
                </Button>
              );
            })}
            {sectionFiles.length === 0 && <Typography color="text.secondary">No {section} available to attach.</Typography>}
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(145px, 1fr))', gap: 2 }}>
              {displayedFiles.map((file) => {
                const selected = selectedIds.includes(file.file_id);
                return (
                  <Button key={file.file_id} variant="outlined" onClick={() => toggleFile(file.file_id)}
                    aria-label={`Select ${file.file_name}`}
                    aria-pressed={selected}
                    sx={{ p: 0, display: 'block', textTransform: 'none', overflow: 'hidden', borderWidth: selected ? 3 : 1, borderColor: selected ? 'primary.main' : 'divider' }}>
                    <Box sx={{ position: 'relative', height: 135, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      <FileThumbnail file={file} />
                      {selected && <CheckCircleIcon color="primary" sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'background.paper', borderRadius: '50%' }} />}
                    </Box>
                    <Typography variant="caption" component="div" sx={{ p: 1, minHeight: 44, overflowWrap: 'anywhere', color: 'text.primary' }}>
                      {fileDisplayName(file)}
                    </Typography>
                  </Button>
                );
              })}
            </Box>
            {folderFiles.length > PAGE_SIZE && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3 }}>
                <Button onClick={() => setPage((current) => current - 1)} disabled={page === 1}>Previous</Button>
                <Typography variant="body2">Page {page} of {Math.ceil(folderFiles.length / PAGE_SIZE)}</Typography>
                <Button onClick={() => setPage((current) => current + 1)} disabled={page * PAGE_SIZE >= folderFiles.length}>Next</Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" sx={{ mr: 'auto', color: 'text.secondary' }}>{selectedIds.length} selected</Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={selectedIds.length === 0} onClick={() => { onAttach(selectedFiles); onClose(); }}>
          Attach {selectedIds.length ? `${selectedIds.length} ${selectedIds.length === 1 ? 'file' : 'files'}` : 'files'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
